// MarketAnalysis.js
import React, { useState, useEffect, useMemo, useContext, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  LinearProgress,
  Chip,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Alert,
  Button,
  useTheme,
  Dialog,
  DialogContent,
  DialogActions,
  useMediaQuery
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';

import {
  TrendingUp,
  Users,
  Target,
  Download,
  RefreshCcw,
  AlertCircle,
  Share2,
  Eye,
  UserCheck,
  ShoppingCart,
  Info,
  ArrowUpRight,
  ArrowDownRight,
  Package,
  History
} from 'lucide-react';

import {
  ResponsiveContainer,
  LineChart as RechartsLineChart,
  PieChart as RechartsPieChart,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  Line,
  Pie,
  Cell,
  Legend
} from 'recharts';

import TouchAppIcon from '@mui/icons-material/TouchApp';

import { api } from '../../services/AGTUps-product-services/config';
import MarketAnalysisService from '../../services/AGTUps-product-services/marketAnalysisService';
import ProductSelector from './ProductSelector';
import MarketAnalysisHistory from './MarketAnalysisHistory';
// Importamos el ProductContext
import { ProductContext } from '../../../../context/ProductContext';
// Importamos el hook de traducción
import { useTranslation } from 'react-i18next';
import AppleSpinner from '../../../common/AppleSpinner';

/* -------------------------------------------------------------------------
   Color System (light/dark).
------------------------------------------------------------------------- */
const colors = {
  light: {
    label: {
      primary: '#000000'
    },
    system: {
      grey: '#8E8E93',
      grey3: '#C7C7CC',
      white: '#FFFFFF',
      black: '#000000',
      green: '#31C859' // Success
    },
    bg: {
      primaryElevated: '#FFFFFF'
    },
    fill: {
      tertiary: '#767680'
    },
    ui: {
      accent: '#007BFE',
      backgroundBlur: '#FFFFFF',
      base: '#F2F2F7'
    },
    grouped: {
      bgPrimaryBase: '#FFFFFF'
    }
  },
  dark: {
    label: {
      primary: '#FFFFFF'
    },
    system: {
      grey: '#8E8E93',
      grey3: '#464649',
      white: '#FFFFFF',
      black: '#000000',
      green: '#2DD257' // Success in dark mode
    },
    bg: {
      primaryElevated: '#000000'
    },
    fill: {
      tertiary: '#767680'
    },
    ui: {
      accent: '#0385FF',
      backgroundBlur: '#282828',
      base: '#272729'
    },
    grouped: {
      bgPrimaryBase: '#000000'
    }
  }
};

/* -------------------------------------------------------------------------
   Function to get navigation colors based on the mode (light/dark).
------------------------------------------------------------------------- */
const navigationColors = (theme) => {
  const mode = theme.palette.mode;
  const currentColors = mode === 'light' ? colors.light : colors.dark;

  return {
    background: currentColors.bg.primaryElevated,
    textDefault: currentColors.label.primary,
    textSecondary: currentColors.system.grey,
    cardBackground: currentColors.ui.base,
    borderColor: currentColors.system.grey3,
    accent: currentColors.ui.accent,
    success: currentColors.system.green,
    error: '#FF3B30',   // iOS style red
    warning: '#FF9500', // iOS style orange/yellow
    secondary: '#AF52DE' // optional iOS style purple
  };
};

/* -------------------------------------------------------------------------
   Apple-like Styled Components.
------------------------------------------------------------------------- */

/**
 * Translucent paper with rounded borders.
 */
const StyledPaper = styled(Paper)(({ theme }) => {
  const navColors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : alpha(navColors.cardBackground, 0.8),
    color: navColors.textDefault,
    padding: theme.spacing(3),
    borderRadius: '16px',
    transition: 'all 0.3s ease',
    boxShadow: 'none',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'blur(20px)',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'blur(20px)',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none'
  };
});

/**
 * Small metric card with translucent background.
 */
const MetricCard = styled(Box)(({ theme }) => {
  const navColors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : alpha(navColors.cardBackground, 0.5),
    padding: theme.spacing(2.5),
    borderRadius: '12px',
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'blur(10px)',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'blur(10px)',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)'
    }
  };
});

/**
 * Select with translucent background and no border.
 */
const StyledSelect = styled(Select)(({ theme }) => {
  const navColors = navigationColors(theme);
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.25)'
      : alpha(navColors.cardBackground, 0.5),
    borderRadius: '12px',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none',
    boxShadow: 'none',
    backdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(8px)' : 'none',
    '& .MuiOutlinedInput-notchedOutline': {
      border: 'none' // Esto elimina el borde
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      border: 'none' // Elimina el borde en hover
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      border: 'none', // Elimina el borde en focus
      boxShadow: `0 0 0 2px ${alpha(navColors.accent, 0.2)}` // Opcional: sutil indicador de focus
    },
    '& .MuiSelect-select': {
      padding: '12px 16px',
      fontSize: '0.95rem',
      fontWeight: 500
    }
  };
});

/**
 * Thin progress bar in Apple style.
 */
const StyledLinearProgress = styled(LinearProgress)(({ theme }) => {
  const navColors = navigationColors(theme);
  return {
    height: 6,
    borderRadius: 3,
    backgroundColor: alpha(navColors.borderColor, 0.2),
    '& .MuiLinearProgress-bar': {
      borderRadius: 3,
      backgroundColor: navColors.accent
    }
  };
});

/**
 * Translucent chip with no border.
 */
const StyledChip = styled(Chip)(({ theme }) => {
  const navColors = navigationColors(theme);
  return {
    borderRadius: '8px',
    backgroundColor: alpha(navColors.accent, 0.1),
    color: navColors.accent,
    '&:hover': {
      backgroundColor: alpha(navColors.accent, 0.15)
    }
  };
});

/**
 * Overlay that appears over the card when updating.
 */
const LoadingOverlay = styled(Box)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1,
    borderRadius: '16px',
    backgroundColor: isWallpaperMode
      ? 'rgba(128, 128, 128, 0.3)'
      : 'rgba(0, 0, 0, 0.1)',
    backdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    WebkitBackdropFilter: isWallpaperMode ? 'blur(10px)' : 'none',
    border: isWallpaperMode ? '1px solid rgba(128, 128, 128, 0.3)' : 'none'
  };
});

/**
 * Apple-style button with transparency and wallpaper mode support.
 */
const AppleStyledButton = styled(Button)(({ theme }) => {
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';
  return {
    backgroundColor: isWallpaperMode ? '#FFFFFF' : '#0385FF',
    color: isWallpaperMode ? '#000000' : '#FFFFFF',
    borderRadius: '28px',
    fontWeight: 600,
    textTransform: 'none',
    padding: theme.spacing(1, 2),
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.85)' : '#0266cc',
      transform: 'translateY(-2px)'
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed'
    }
  };
});

/**
 * Reusable centered spinner component
 */
const CenteredSpinner = () => (
  <Box sx={{ 
    display: 'flex', 
    justifyContent: 'center', 
    alignItems: 'center', 
    width: '100%',
    py: 5 
  }}>
    <AppleSpinner />
  </Box>
);

/**
 * Large centered button to generate AI analysis if none exists with wallpaper mode support.
 */
const CenteredAnalysisButton = ({ onClick, disabled, isGenerating, t }) => {
  const theme = useTheme();
  const isWallpaperMode = theme.palette.actualMode === 'wallpaper';

  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      sx={{
        backgroundColor: isWallpaperMode ? '#FFFFFF' : '#0385FF',
        color: isWallpaperMode ? '#000000' : '#FFFFFF',
        backdropFilter: 'blur(10px)',
        borderRadius: '28px',
        fontWeight: 600,
        textTransform: 'none',
        padding: '12px 32px',
        fontSize: '1.1rem',
        transition: 'all 0.3s ease',
        '&:hover': {
          backgroundColor: isWallpaperMode ? 'rgba(255, 255, 255, 0.85)' : '#0266cc',
          transform: 'translateY(-2px)',
          boxShadow: isWallpaperMode
            ? '0 4px 12px rgba(255, 255, 255, 0.3)'
            : '0 4px 12px rgba(3, 133, 255, 0.2)'
        },
        '&:disabled': {
          opacity: 0.6,
          cursor: 'not-allowed'
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
        {isGenerating && <AppleSpinner size={20} color="inherit" />}
        {isGenerating ? t('agtUps.marketAnalysis.generatingAnalysis') : t('agtUps.marketAnalysis.generateAnalysis')}
      </Box>
    </Button>
  );
};

/**
 * IMPORTANT: Container with internal scroll and fixed height.
 */
const StyledContainer = styled(Box)(() => ({
  backgroundColor: 'transparent',
  padding: '24px',
  width: '100%',
  height: 'calc(100vh - 180px)',
  overflowY: 'auto',
  '&::-webkit-scrollbar': {
    width: '8px'
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent'
  },
  '&::-webkit-scrollbar-thumb': {
    background: 'rgba(0, 0, 0, 0.2)',
    borderRadius: '4px'
  }
}));

/* -------------------------------------------------------------------------
   Main Component: MarketAnalysis
------------------------------------------------------------------------- */
const MarketAnalysis = () => {
  const theme = useTheme();
  const navColors = navigationColors(theme);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation(); // Hook de traducción

  // Consumiendo el contexto global de productos
  const { products, loading: productsLoading } = useContext(ProductContext);

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [timeRange, setTimeRange] = useState('6m');
  const [analysisType, setAnalysisType] = useState('overview');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [marketData, setMarketData] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [updating, setUpdating] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const [generatingAI, setGeneratingAI] = useState(false);

  // Para mostrar/ocultar el diálogo de historial
  const [showHistory, setShowHistory] = useState(false);

  /* -----------------------------------------------------------------------
     Function to load the analysis.
  ------------------------------------------------------------------------ */
  const loadMarketData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const existingAnalysis = await MarketAnalysisService.getMarketAnalysisByProduct(
        selectedProduct.id,
        { timeRange }
      );

      if (!existingAnalysis.data) {
        setMarketData(null);
      } else {
        setMarketData(existingAnalysis.data);
      }

      setLastUpdated(new Date());
    } catch (err) {
      setError(t('agtUps.marketAnalysis.errors.loadingMarketData', { message: err.message }));
    } finally {
      setLoading(false);
    }
  }, [selectedProduct, timeRange, t]);

  /* -----------------------------------------------------------------------
     Load existing analysis the first time a product is selected.
  ------------------------------------------------------------------------ */
  useEffect(() => {
    if (selectedProduct?.id && !initialLoadDone) {
      loadMarketData();
      setInitialLoadDone(true);
    }
  }, [selectedProduct, initialLoadDone, loadMarketData]);

  /* -----------------------------------------------------------------------
     Load data every time the timeRange changes (if analysis exists).
  ------------------------------------------------------------------------ */
  useEffect(() => {
    if (selectedProduct?.id && initialLoadDone) {
      loadMarketData();
    }
  }, [selectedProduct, timeRange, initialLoadDone, loadMarketData]);

  /* -----------------------------------------------------------------------
     Generate (or refresh) a new AI analysis (when clicking "refresh").
  ------------------------------------------------------------------------ */
  const handleRefreshData = async () => {
    if (!selectedProduct) return;
    try {
      setUpdating(true);
      const response = await MarketAnalysisService.createAnalysis(selectedProduct.id);
      setMarketData(response.data);
      setLastUpdated(new Date());
    } catch (err) {
      setError(t('agtUps.marketAnalysis.errors.updatingAnalysis', { message: err.message }));
    } finally {
      setUpdating(false);
    }
  };

  /* -----------------------------------------------------------------------
     Export to JSON.
  ------------------------------------------------------------------------ */
  const handleExportData = () => {
    if (!selectedProduct || !marketData) return;
    try {
      const data = {
        marketAnalysis: marketData,
        product: selectedProduct,
        exportDate: new Date().toISOString(),
        timeRange,
        metadata: {
          version: '1.0',
          generatedBy: 'AGT Market Analysis',
          lastUpdated: lastUpdated?.toISOString()
        }
      };

      const blob = new Blob([JSON.stringify(data, null, 2)], {
        type: 'application/json'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `market-analysis-${selectedProduct.id}-${timeRange}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(t('agtUps.marketAnalysis.errors.exportingData', { message: err.message }));
    }
  };

  /* -----------------------------------------------------------------------
     Generate AI analysis (initial "Generate Analysis with AGT" button).
  ------------------------------------------------------------------------ */
  const handleGenerateAIAnalysis = async () => {
    if (!selectedProduct?.id) return;
    try {
      setGeneratingAI(true);
      setError(null);

      const response = await api.post(`/market/generate/${selectedProduct.id}`);
      if (response.data?.success) {
        setMarketData(response.data.data);
        setLastUpdated(new Date());
        setInitialLoadDone(true);
      }
    } catch (err) {
      setError(t('agtUps.marketAnalysis.errors.generatingAI', { message: err.message }));
    } finally {
      setGeneratingAI(false);
    }
  };

  /* -----------------------------------------------------------------------
     Format data for charts (line and pie charts).
  ------------------------------------------------------------------------ */
  const formattedMarketData = useMemo(() => {
    if (!marketData || !selectedProduct) return null;
    return MarketAnalysisService.formatChartData(marketData, selectedProduct);
  }, [marketData, selectedProduct]);

  /* -----------------------------------------------------------------------
     Calculate metrics (marketFit, growthPotential, etc.).
  ------------------------------------------------------------------------ */
  const metrics = useMemo(() => {
    if (!marketData) return null;
    return MarketAnalysisService.calculateMetrics(marketData);
  }, [marketData]);

  /* -----------------------------------------------------------------------
     Colors for the pie slices (competitors).
  ------------------------------------------------------------------------ */
  const competitorColors = useMemo(() => {
    return [
      navColors.accent,
      navColors.success,
      navColors.warning,
      navColors.secondary,
      navColors.error
    ];
  }, [
    navColors.accent,
    navColors.success,
    navColors.warning,
    navColors.secondary,
    navColors.error
  ]);

  /* -----------------------------------------------------------------------
     Main Render.
  ------------------------------------------------------------------------ */
  return (
    <StyledContainer>
      <Box sx={{ '& > *:not(:last-child)': { mb: 4 } }}>
        {/* Row: Product Selector + "Generate Analysis with AGT" Button */}
        <Grid container spacing={2} sx={{ mb: 2, alignItems: 'center' }}>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <ProductSelector
                  products={products}
                  selectedProduct={selectedProduct}
                  onProductSelect={setSelectedProduct}
                  loading={productsLoading}
                  compact
                />
              </Box>
              {selectedProduct && (
                <AppleStyledButton
                  onClick={handleGenerateAIAnalysis}
                  disabled={generatingAI || productsLoading}
                >
                  {generatingAI ? t('agtUps.marketAnalysis.generatingAnalysis') : t('agtUps.marketAnalysis.generateAnalysis')}
                </AppleStyledButton>
              )}
            </Box>
          </Grid>
        </Grid>

        {/* Global loading state from context */}
        {productsLoading && <CenteredSpinner />}

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            onClose={() => setError(null)}
            sx={{
              borderRadius: '12px',
              backgroundColor: alpha(theme.palette.error.main, 0.1),
              color: theme.palette.error.main,
              backdropFilter: 'blur(10px)'
            }}
          >
            {error}
          </Alert>
        )}

        {/* Case 1: No product selected */}
        {!selectedProduct && (
          <StyledPaper sx={{ textAlign: 'center', py: 8 }}>
            <Package
              size={48}
              style={{
                marginBottom: '16px',
                color: alpha(navColors.textSecondary, 0.6)
              }}
            />
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              {t('agtUps.marketAnalysis.noProductSelected.title')}
            </Typography>
            <Typography variant="body2" sx={{ color: navColors.textSecondary }}>
              {t('agtUps.marketAnalysis.noProductSelected.description')}
            </Typography>
          </StyledPaper>
        )}

        {/* Case 2: Product selected but NO analysis available */}
        {selectedProduct && !marketData && (
          <>
            {loading ? (
              <CenteredSpinner />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '60vh',
                  textAlign: 'center',
                  px: 3
                }}
              >
                <Typography
                  variant="h5"
                  sx={{
                    fontWeight: 600,
                    letterSpacing: '-0.02em',
                    marginBottom: 2,
                    color: navColors.textDefault
                  }}
                >
                  {t('agtUps.marketAnalysis.noAnalysisAvailable.title')}
                </Typography>
                <Typography
                  variant="body1"
                  sx={{
                    color: navColors.textSecondary,
                    maxWidth: 600,
                    marginBottom: 4
                  }}
                >
                  {t('agtUps.marketAnalysis.noAnalysisAvailable.description', { productName: selectedProduct.name })}
                </Typography>
                <CenteredAnalysisButton
                  onClick={handleGenerateAIAnalysis}
                  disabled={generatingAI}
                  isGenerating={generatingAI}
                  t={t}
                />
              </Box>
            )}
          </>
        )}

        {/* Case 3: Analysis available => Display the analysis */}
        {selectedProduct && marketData && (
          <>
            {/* Controls Bar: Time Range, Analysis Type, Refresh, Export, History */}
            <Grid container spacing={3} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel
                    sx={{
                      color: navColors.textSecondary,
                      '&.Mui-focused': {
                        color: navColors.accent
                      }
                    }}
                  >
                    {t('agtUps.marketAnalysis.controls.timeRange')}
                  </InputLabel>
                  <StyledSelect
                    value={timeRange}
                    label={t('agtUps.marketAnalysis.controls.timeRange')}
                    onChange={(e) => setTimeRange(e.target.value)}
                    disabled={loading || updating}
                  >
                    <MenuItem value="1m">{t('agtUps.marketAnalysis.controls.timeRanges.1m')}</MenuItem>
                    <MenuItem value="3m">{t('agtUps.marketAnalysis.controls.timeRanges.3m')}</MenuItem>
                    <MenuItem value="6m">{t('agtUps.marketAnalysis.controls.timeRanges.6m')}</MenuItem>
                    <MenuItem value="1y">{t('agtUps.marketAnalysis.controls.timeRanges.1y')}</MenuItem>
                  </StyledSelect>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel
                    sx={{
                      color: navColors.textSecondary,
                      '&.Mui-focused': {
                        color: navColors.accent
                      }
                    }}
                  >
                    {t('agtUps.marketAnalysis.controls.analysisType')}
                  </InputLabel>
                  <StyledSelect
                    value={analysisType}
                    label={t('agtUps.marketAnalysis.controls.analysisType')}
                    onChange={(e) => setAnalysisType(e.target.value)}
                    disabled={loading || updating}
                  >
                    <MenuItem value="overview">{t('agtUps.marketAnalysis.controls.analysisTypes.overview')}</MenuItem>
                    <MenuItem value="marketShareTrends">{t('agtUps.marketAnalysis.controls.analysisTypes.marketShareTrends')}</MenuItem>
                    <MenuItem value="marketPosition">{t('agtUps.marketAnalysis.controls.analysisTypes.marketPosition')}</MenuItem>
                    <MenuItem value="keyMetrics">{t('agtUps.marketAnalysis.controls.analysisTypes.keyMetrics')}</MenuItem>
                    <MenuItem value="marketShareDistribution">{t('agtUps.marketAnalysis.controls.analysisTypes.marketShareDistribution')}</MenuItem>
                    <MenuItem value="marketInsights">{t('agtUps.marketAnalysis.controls.analysisTypes.marketInsights')}</MenuItem>
                  </StyledSelect>
                </FormControl>
              </Grid>

              <Grid item xs={12} md={6} display="flex" justifyContent="flex-end" gap={1}>
                <Tooltip title={t('agtUps.marketAnalysis.controls.history')}>
                  <AppleStyledButton
                    onClick={() => setShowHistory(true)}
                    startIcon={<History size={16} />}
                    disabled={!selectedProduct}
                  >
                    {t('agtUps.marketAnalysis.controls.history')}
                  </AppleStyledButton>
                </Tooltip>

                <Tooltip
                  title={
                    lastUpdated
                      ? t('agtUps.marketAnalysis.controls.lastUpdated', { date: lastUpdated.toLocaleString() })
                      : ''
                  }
                >
                  <IconButton
                    onClick={handleRefreshData}
                    disabled={updating || loading}
                    sx={{
                      color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : navColors.accent,
                      '&:hover': {
                        backgroundColor: theme.palette.actualMode === 'wallpaper'
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'rgba(3, 133, 255, 0.08)'
                      }
                    }}
                  >
                    <RefreshCcw size={20} />
                  </IconButton>
                </Tooltip>

                <Tooltip title={t('agtUps.marketAnalysis.controls.export')}>
                  <IconButton
                    onClick={handleExportData}
                    disabled={loading || !marketData}
                    sx={{
                      color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : navColors.accent,
                      '&:hover': {
                        backgroundColor: theme.palette.actualMode === 'wallpaper'
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'rgba(3, 133, 255, 0.08)'
                      }
                    }}
                  >
                    <Download size={20} />
                  </IconButton>
                </Tooltip>

                <Tooltip title={t('agtUps.marketAnalysis.controls.share')}>
                  <IconButton
                    disabled={loading}
                    sx={{
                      color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : navColors.accent,
                      '&:hover': {
                        backgroundColor: theme.palette.actualMode === 'wallpaper'
                          ? 'rgba(255, 255, 255, 0.1)'
                          : 'rgba(3, 133, 255, 0.08)'
                      }
                    }}
                  >
                    <Share2 size={20} />
                  </IconButton>
                </Tooltip>
              </Grid>
            </Grid>

            {/* History Dialog */}
            <Dialog
              open={showHistory}
              onClose={() => setShowHistory(false)}
              maxWidth="lg"
              fullWidth
              sx={{
                '& .MuiDialog-paper': {
                  backgroundColor: theme.palette.actualMode === 'wallpaper'
                    ? 'rgba(128, 128, 128, 0.3)'
                    : (theme.palette.mode === 'dark' ? '#1C1C1E' : '#FFFFFF'),
                  backdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                  WebkitBackdropFilter: theme.palette.actualMode === 'wallpaper' ? 'blur(10px)' : 'none',
                  border: theme.palette.actualMode === 'wallpaper' ? '1px solid rgba(128, 128, 128, 0.3)' : 'none'
                }
              }}
            >
              <DialogContent sx={{ p: 0 }}>
                {selectedProduct && (
                  <MarketAnalysisHistory productId={selectedProduct.id} />
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  onClick={() => setShowHistory(false)}
                  sx={{
                    color: theme.palette.actualMode === 'wallpaper' ? '#FFFFFF' : navColors.accent,
                    '&:hover': {
                      backgroundColor: theme.palette.actualMode === 'wallpaper'
                        ? 'rgba(255, 255, 255, 0.1)'
                        : 'rgba(3, 133, 255, 0.08)'
                    }
                  }}
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>

            {loading && !marketData ? (
              <CenteredSpinner />
            ) : (
              marketData && (
                <>
                  {/* Render based on analysisType */}

                  {/* 1. OVERVIEW => All in one */}
                  {analysisType === 'overview' && (
                    <>
                      {/* Main metrics block */}
                      <StyledPaper>
                        <Box sx={{ mb: 4 }}>
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 700,
                              letterSpacing: '-0.02em',
                              mb: 1
                            }}
                          >
                            {selectedProduct.name}
                          </Typography>
                          <Typography variant="body1" sx={{ color: navColors.textSecondary }}>
                            {t('agtUps.marketAnalysis.title')}
                          </Typography>
                        </Box>

                        {metrics && (
                          <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={3}>
                              <MetricCard>
                                <Tooltip
                                  title={t('agtUps.marketAnalysis.metrics.marketShare.tooltip')}
                                  arrow
                                  placement="top"
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: navColors.textSecondary,
                                      mb: 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      cursor: 'help'
                                    }}
                                  >
                                    {t('agtUps.marketAnalysis.metrics.marketShare.title')}
                                    <Info size={16} />
                                  </Typography>
                                </Tooltip>
                                <Typography
                                  variant="h4"
                                  sx={{
                                    fontWeight: 700,
                                    letterSpacing: '-0.02em'
                                  }}
                                >
                                  {marketData?.marketShare}%
                                </Typography>
                                <StyledLinearProgress
                                  variant="determinate"
                                  value={marketData?.marketShare || 0}
                                  sx={{ mt: 2 }}
                                />
                              </MetricCard>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                              <MetricCard>
                                <Tooltip
                                  title={t('agtUps.marketAnalysis.metrics.marketFit.tooltip')}
                                  arrow
                                  placement="top"
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: navColors.textSecondary,
                                      mb: 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      cursor: 'help'
                                    }}
                                  >
                                    {t('agtUps.marketAnalysis.metrics.marketFit.title')}
                                    <Info size={16} />
                                  </Typography>
                                </Tooltip>
                                <Typography
                                  variant="h4"
                                  sx={{
                                    fontWeight: 700,
                                    letterSpacing: '-0.02em'
                                  }}
                                >
                                  {metrics?.marketFit || 0}%
                                </Typography>
                                <StyledLinearProgress
                                  variant="determinate"
                                  value={metrics?.marketFit || 0}
                                  sx={{ mt: 2 }}
                                />
                              </MetricCard>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                              <MetricCard>
                                <Tooltip
                                  title={t('agtUps.marketAnalysis.metrics.growthRate.tooltip')}
                                  arrow
                                  placement="top"
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: navColors.textSecondary,
                                      mb: 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      cursor: 'help'
                                    }}
                                  >
                                    {t('agtUps.marketAnalysis.metrics.growthRate.title')}
                                    <Info size={16} />
                                  </Typography>
                                </Tooltip>
                                <Typography
                                  variant="h4"
                                  sx={{
                                    fontWeight: 700,
                                    letterSpacing: '-0.02em'
                                  }}
                                >
                                  {metrics?.growthPotential?.potential || 0}%
                                </Typography>
                                <StyledLinearProgress
                                  variant="determinate"
                                  value={metrics?.growthPotential?.potential || 0}
                                  sx={{ mt: 2 }}
                                />
                              </MetricCard>
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                              <MetricCard>
                                <Tooltip
                                  title={t('agtUps.marketAnalysis.metrics.marketReach.tooltip')}
                                  arrow
                                  placement="top"
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      color: navColors.textSecondary,
                                      mb: 1,
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      cursor: 'help'
                                    }}
                                  >
                                    {t('agtUps.marketAnalysis.metrics.marketReach.title')}
                                    <Info size={16} />
                                  </Typography>
                                </Tooltip>
                                <Typography
                                  variant="h4"
                                  sx={{
                                    fontWeight: 700,
                                    letterSpacing: '-0.02em'
                                  }}
                                >
                                  {Object.keys(marketData?.geographicDistribution || {}).length} {t('agtUps.marketAnalysis.metrics.regions')}
                                </Typography>
                                <StyledLinearProgress
                                  variant="determinate"
                                  value={Object.keys(marketData?.geographicDistribution || {}).length * 10}
                                  sx={{ mt: 2 }}
                                />
                              </MetricCard>
                            </Grid>
                          </Grid>
                        )}
                      </StyledPaper>

                      {/* Market Share Trends + Market Position + Key Metrics */}
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={8}>
                          <StyledPaper style={{ position: 'relative' }}>
                            {updating && (
                              <LoadingOverlay>
                                <AppleSpinner />
                              </LoadingOverlay>
                            )}
                            <Box sx={{ mb: 3 }}>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                  {t('agtUps.marketAnalysis.marketShareTrends.title')}
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Tooltip title={t('agtUps.marketAnalysis.marketShareTrends.chips.marketShare')} arrow>
                                    <StyledChip
                                      size="small"
                                      icon={<Eye size={14} />}
                                      label={t('agtUps.marketAnalysis.marketShareTrends.chips.marketShare')}
                                    />
                                  </Tooltip>
                                  <Tooltip title={t('agtUps.marketAnalysis.marketShareTrends.chips.competitors')} arrow>
                                    <StyledChip
                                      size="small"
                                      icon={<Users size={14} />}
                                      label={t('agtUps.marketAnalysis.marketShareTrends.chips.competitors')}
                                    />
                                  </Tooltip>
                                  <Tooltip title={t('agtUps.marketAnalysis.marketShareTrends.chips.marketPotential')} arrow>
                                    <StyledChip
                                      size="small"
                                      icon={<Target size={14} />}
                                      label={t('agtUps.marketAnalysis.marketShareTrends.chips.marketPotential')}
                                    />
                                  </Tooltip>
                                </Box>
                              </Box>
                              <Alert
                                severity="info"
                                sx={{
                                  backgroundColor: alpha(navColors.accent, 0.1),
                                  color: navColors.textDefault
                                }}
                              >
                                <Typography variant="body2">
                                  {t('agtUps.marketAnalysis.marketShareTrends.description')}
                                </Typography>
                              </Alert>
                            </Box>

                            <Box sx={{ height: 320 }}>
                              {formattedMarketData?.marketShare?.data ? (
                                <ResponsiveContainer width="100%" height="100%">
                                  <RechartsLineChart data={formattedMarketData.marketShare.data}>
                                    <CartesianGrid strokeDasharray="3 3" stroke={alpha(navColors.textDefault, 0.2)} />
                                    <XAxis dataKey="name" tick={{ fill: navColors.textDefault }} />
                                    <YAxis tick={{ fill: navColors.textDefault }} />
                                    <RechartsTooltip
                                      contentStyle={{
                                        backgroundColor: alpha(navColors.cardBackground, 0.8),
                                        borderRadius: '8px',
                                        color: navColors.textDefault
                                      }}
                                    />
                                    <Line
                                      type="monotone"
                                      dataKey="marketShare"
                                      name={t('agtUps.marketAnalysis.marketShareTrends.chips.marketShare')}
                                      stroke={navColors.accent}
                                      strokeWidth={2}
                                      dot={{ r: 4 }}
                                      activeDot={{ r: 8 }}
                                    />
                                    <Line
                                      type="monotone"
                                      dataKey="competitors"
                                      name={t('agtUps.marketAnalysis.marketShareTrends.chips.competitors')}
                                      stroke={navColors.secondary}
                                      strokeWidth={2}
                                      dot={{ r: 4 }}
                                    />
                                    <Line
                                      type="monotone"
                                      dataKey="potential"
                                      name={t('agtUps.marketAnalysis.marketShareTrends.chips.marketPotential')}
                                      stroke={navColors.success}
                                      strokeWidth={2}
                                      dot={{ r: 4 }}
                                    />
                                    <Legend wrapperStyle={{ color: navColors.textDefault }} />
                                  </RechartsLineChart>
                                </ResponsiveContainer>
                              ) : (
                                <Box
                                  sx={{
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Typography sx={{ color: navColors.textSecondary }}>
                                    {t('agtUps.marketAnalysis.marketShareTrends.noData')}
                                  </Typography>
                                </Box>
                              )}
                            </Box>
                          </StyledPaper>
                        </Grid>

                        <Grid item xs={12} md={4}>
                          <Grid container spacing={3}>
                            <Grid item xs={12}>
                              <StyledPaper>
                                <Box sx={{ mb: 3 }}>
                                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                                    {t('agtUps.marketAnalysis.marketPosition.title')}
                                  </Typography>
                                  <Alert
                                    severity="info"
                                    sx={{
                                      backgroundColor: alpha(navColors.accent, 0.1),
                                      color: navColors.textDefault
                                    }}
                                  >
                                    <Typography variant="body2">
                                      {t('agtUps.marketAnalysis.marketPosition.description')}
                                    </Typography>
                                  </Alert>
                                </Box>

                                {marketData ? (
                                  <Box sx={{ display: 'grid', rowGap: 2 }}>
                                    <Tooltip title={t('agtUps.marketAnalysis.marketPosition.tooltips.marketShare')} arrow placement="top">
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          marginBottom: 1,
                                          cursor: 'help'
                                        }}
                                      >
                                        <Typography variant="body2">{t('agtUps.marketAnalysis.marketPosition.metrics.marketShare')}</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {metrics?.yearOverYear?.growth > 0 ? (
                                            <ArrowUpRight size={16} style={{ color: navColors.success }} />
                                          ) : (
                                            <ArrowDownRight size={16} style={{ color: navColors.error }} />
                                          )}
                                          <Typography variant="body2" sx={{ color: metrics?.yearOverYear?.growth > 0 ? navColors.success : navColors.error }}>
                                            {marketData.marketShare}%
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Tooltip>
                                    <StyledLinearProgress variant="determinate" value={marketData.marketShare} />

                                    <Tooltip title={t('agtUps.marketAnalysis.marketPosition.tooltips.competitivePosition')} arrow placement="top">
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          marginBottom: 1,
                                          cursor: 'help'
                                        }}
                                      >
                                        <Typography variant="body2">{t('agtUps.marketAnalysis.marketPosition.metrics.competitivePosition')}</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          <ArrowUpRight size={16} style={{ color: navColors.success }} />
                                          <Typography variant="body2" sx={{ color: navColors.success }}>
                                            {metrics?.competitivePosition || 0}%
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Tooltip>
                                    <StyledLinearProgress variant="determinate" value={metrics?.competitivePosition || 0} />

                                    <Tooltip title={t('agtUps.marketAnalysis.marketPosition.tooltips.marketPenetration')} arrow placement="top">
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          justifyContent: 'space-between',
                                          marginBottom: 1,
                                          cursor: 'help'
                                        }}
                                      >
                                        <Typography variant="body2">{t('agtUps.marketAnalysis.marketPosition.metrics.marketPenetration')}</Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                          {metrics?.marketPenetration > 0 ? (
                                            <ArrowUpRight size={16} style={{ color: navColors.success }} />
                                          ) : (
                                            <ArrowDownRight size={16} style={{ color: navColors.error }} />
                                          )}
                                          <Typography variant="body2" sx={{ color: metrics?.marketPenetration > 0 ? navColors.success : navColors.error }}>
                                            {metrics?.marketPenetration || 0}%
                                          </Typography>
                                        </Box>
                                      </Box>
                                    </Tooltip>
                                    <StyledLinearProgress variant="determinate" value={metrics?.marketPenetration || 0} />
                                  </Box>
                                ) : (
                                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                                    <AppleSpinner />
                                  </Box>
                                )}
                              </StyledPaper>
                            </Grid>

                            <Grid item xs={12}>
                              <StyledPaper>
                                <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: 2 }}>
                                  {t('agtUps.marketAnalysis.keyMetrics.title')}
                                </Typography>
                                <Alert
                                  severity="info"
                                  sx={{
                                    mb: 3,
                                    backgroundColor: alpha(navColors.accent, 0.1),
                                    color: navColors.textDefault,
                                    '& .MuiAlert-icon': {
                                      color: navColors.accent
                                    }
                                  }}
                                >
                                  <Typography variant="body2">
                                    {t('agtUps.marketAnalysis.keyMetrics.description')}
                                  </Typography>
                                </Alert>
                                <Grid container spacing={2}>
                                  <Grid item xs={6}>
                                    <Tooltip title={t('agtUps.marketAnalysis.keyMetrics.marketFit.tooltip')} arrow placement="top">
                                      <MetricCard sx={{ cursor: 'help' }}>
                                        <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                          <UserCheck size={20} style={{ color: navColors.accent }} />
                                          <Typography variant="body2">{t('agtUps.marketAnalysis.keyMetrics.marketFit.title')}</Typography>
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                          {metrics?.marketFit || 'N/A'}%
                                        </Typography>
                                      </MetricCard>
                                    </Tooltip>
                                  </Grid>

                                  <Grid item xs={6}>
                                    <Tooltip title={t('agtUps.marketAnalysis.keyMetrics.growthPotential.tooltip')} arrow placement="top">
                                      <MetricCard sx={{ cursor: 'help' }}>
                                        <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                          <ShoppingCart size={20} style={{ color: navColors.secondary }} />
                                          <Typography variant="body2">{t('agtUps.marketAnalysis.keyMetrics.growthPotential.title')}</Typography>
                                        </Box>
                                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                          {metrics?.growthPotential?.potential || 'N/A'}%
                                        </Typography>
                                        {metrics?.growthPotential?.confidence && (
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                            <Typography variant="caption" sx={{ color: navColors.textSecondary }}>
                                              {t('agtUps.marketAnalysis.keyMetrics.growthPotential.confidence', { confidence: metrics.growthPotential.confidence })}
                                            </Typography>
                                          </Box>
                                        )}
                                      </MetricCard>
                                    </Tooltip>
                                  </Grid>
                                </Grid>
                              </StyledPaper>
                            </Grid>
                          </Grid>
                        </Grid>
                      </Grid>

                      {/* Market Share Distribution + Market Insights */}
                      <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                          <StyledPaper>
                            <Box sx={{ mb: 3 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                                {t('agtUps.marketAnalysis.marketShareDistribution.title')}
                              </Typography>
                              <Alert
                                severity="info"
                                sx={{
                                  backgroundColor: alpha(navColors.accent, 0.1),
                                  color: navColors.textDefault,
                                  mb: 2,
                                  '& .MuiAlert-icon': {
                                    color: navColors.accent
                                  }
                                }}
                              >
                                <Typography variant="body2">
                                  {t('agtUps.marketAnalysis.marketShareDistribution.description')}
                                </Typography>
                              </Alert>
                            </Box>

                            <Box sx={{ height: 320 }}>
                              {formattedMarketData?.competitors ? (
                                <ResponsiveContainer width="100%" height="100%">
                                  <RechartsPieChart>
                                    <Pie
                                      data={formattedMarketData.competitors}
                                      cx="50%"
                                      cy="50%"
                                      innerRadius={60}
                                      outerRadius={100}
                                      dataKey="value"
                                      labelLine={false}
                                      label={({ name, percent }) =>
                                        `${name} (${(percent * 100).toFixed(1)}%)`
                                      }
                                    >
                                      {formattedMarketData.competitors.map((entry, index) => (
                                        <Cell
                                          key={`cell-${index}`}
                                          fill={competitorColors[index % competitorColors.length]}
                                          className="transition-all duration-300 hover:opacity-80"
                                        />
                                      ))}
                                    </Pie>
                                    <RechartsTooltip
                                      content={({ active, payload }) => {
                                        if (!active || !payload?.[0]) return null;
                                        const data = payload[0].payload;
                                        return (
                                          <Box
                                            sx={{
                                              backgroundColor: alpha(navColors.cardBackground, 0.9),
                                              p: 2,
                                              borderRadius: '8px',
                                              color: navColors.textDefault
                                            }}
                                          >
                                            <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                              {data.name}
                                            </Typography>
                                            <Typography variant="body2">
                                              {t('agtUps.marketAnalysis.marketShareDistribution.tooltip.marketShare', { value: data.value.toFixed(1) })}
                                            </Typography>
                                            {data.strengths?.length > 0 && (
                                              <Box sx={{ mt: 1 }}>
                                                <Typography variant="caption" sx={{ color: navColors.success }}>
                                                  {t('agtUps.marketAnalysis.marketShareDistribution.tooltip.strengths', { strengths: data.strengths.join(', ') })}
                                                </Typography>
                                              </Box>
                                            )}
                                            {data.weaknesses?.length > 0 && (
                                              <Box sx={{ mt: 1 }}>
                                                <Typography variant="caption" sx={{ color: navColors.error }}>
                                                  {t('agtUps.marketAnalysis.marketShareDistribution.tooltip.weaknesses', { weaknesses: data.weaknesses.join(', ') })}
                                                </Typography>
                                              </Box>
                                            )}
                                          </Box>
                                        );
                                      }}
                                    />
                                    <Legend verticalAlign="bottom" height={36} wrapperStyle={{ color: navColors.textDefault }} />
                                  </RechartsPieChart>
                                </ResponsiveContainer>
                              ) : (
                                <Box
                                  sx={{
                                    height: '100%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center'
                                  }}
                                >
                                  <Typography sx={{ color: navColors.textSecondary }}>
                                    {t('agtUps.marketAnalysis.marketShareDistribution.noData')}
                                  </Typography>
                                </Box>
                              )}
                            </Box>

                            {/* Interactive legend */}
                            <Box sx={{ mt: 2, textAlign: 'center' }}>
                              {isMobile ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                                  <TouchAppIcon style={{ color: navColors.textSecondary }} />
                                  <Typography variant="caption" sx={{ color: navColors.textSecondary }}>
                                    {t('agtUps.marketAnalysis.marketShareDistribution.tapToView')}
                                  </Typography>
                                </Box>
                              ) : (
                                <Typography variant="caption" sx={{ color: navColors.textSecondary }}>
                                  {t('agtUps.marketAnalysis.marketShareDistribution.clickToView')}
                                </Typography>
                              )}
                            </Box>
                          </StyledPaper>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          <StyledPaper>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                {t('agtUps.marketAnalysis.marketInsights.title')}
                              </Typography>
                              <StyledChip label={t('agtUps.marketAnalysis.marketInsights.aiGenerated')} size="small" />
                            </Box>

                            {marketData?.insights ? (
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Alert
                                  severity="info"
                                  sx={{
                                    backgroundColor: alpha(navColors.accent, 0.1),
                                    color: navColors.textDefault,
                                    mb: 3,
                                    '& .MuiAlert-icon': {
                                      color: navColors.accent
                                    }
                                  }}
                                >
                                  <Typography variant="body2" sx={{ mb: 2 }}>
                                    {t('agtUps.marketAnalysis.marketInsights.description')}
                                  </Typography>
                                </Alert>

                                {/* Growth Opportunities */}
                                {marketData.insights.opportunities?.map((opportunity, index) => (
                                  <Alert
                                    key={index}
                                    severity="info"
                                    icon={<TrendingUp />}
                                    sx={{
                                      backgroundColor: alpha(navColors.accent, 0.1),
                                      color: navColors.textDefault,
                                      backdropFilter: 'blur(8px)'
                                    }}
                                  >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                      {t('agtUps.marketAnalysis.marketInsights.components.growthOpportunity')}
                                    </Typography>
                                    <Typography variant="body2">
                                      {opportunity.description}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                      <StyledChip 
                                        label={t('agtUps.marketAnalysis.marketInsights.components.impact', { value: opportunity.impact })} 
                                        size="small" 
                                      />
                                      {opportunity.timeframe && (
                                        <Chip
                                          label={opportunity.timeframe}
                                          size="small"
                                          sx={{ borderRadius: '8px' }}
                                        />
                                      )}
                                    </Box>
                                  </Alert>
                                ))}

                                {/* Potential Threats */}
                                {marketData.insights.threats?.map((threat, index) => (
                                  <Alert
                                    key={index}
                                    severity="warning"
                                    icon={<AlertCircle />}
                                    sx={{
                                      backgroundColor: alpha(navColors.warning, 0.1),
                                      color: navColors.textDefault,
                                      backdropFilter: 'blur(8px)'
                                    }}
                                  >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                      {t('agtUps.marketAnalysis.marketInsights.components.marketThreat')}
                                    </Typography>
                                    <Typography variant="body2">
                                      {threat.description}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                      <StyledChip
                                        label={t('agtUps.marketAnalysis.marketInsights.components.severity', { value: threat.severity })}
                                        size="small"
                                        sx={{
                                          backgroundColor: alpha(navColors.warning, 0.15),
                                          color: navColors.warning
                                        }}
                                      />
                                      <StyledChip
                                        label={t('agtUps.marketAnalysis.marketInsights.components.probability', { value: threat.probability })}
                                        size="small"
                                        sx={{
                                          backgroundColor: alpha(navColors.warning, 0.15),
                                          color: navColors.warning
                                        }}
                                      />
                                    </Box>
                                  </Alert>
                                ))}

                                {/* Strategic Recommendations */}
                                {marketData.insights.recommendations?.map((recommendation, index) => (
                                  <Alert
                                    key={index}
                                    severity="success"
                                    icon={<Info />}
                                    sx={{
                                      backgroundColor: alpha(navColors.success, 0.1),
                                      color: navColors.textDefault,
                                      backdropFilter: 'blur(8px)'
                                    }}
                                  >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                      {t('agtUps.marketAnalysis.marketInsights.components.strategicRecommendation')}
                                    </Typography>
                                    <Typography variant="body2">
                                      {recommendation.strategy}
                                    </Typography>
                                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                      <StyledChip
                                        label={t('agtUps.marketAnalysis.marketInsights.components.priority', { value: recommendation.priority })}
                                        size="small"
                                        sx={{
                                          backgroundColor: alpha(navColors.success, 0.15),
                                          color: navColors.success
                                        }}
                                      />
                                      <Chip
                                        label={recommendation.expectedImpact}
                                        size="small"
                                        sx={{ borderRadius: '8px' }}
                                      />
                                    </Box>
                                  </Alert>
                                ))}
                              </Box>
                            ) : (
                              <Box>
                                <Alert
                                  severity="info"
                                  sx={{
                                    backgroundColor: alpha(navColors.accent, 0.1),
                                    color: navColors.textDefault,
                                    mb: 3,
                                    '& .MuiAlert-icon': {
                                      color: navColors.accent
                                    }
                                  }}
                                >
                                  <Typography variant="body2" sx={{ mb: 2 }}>
                                    {t('agtUps.marketAnalysis.marketInsights.description')}
                                  </Typography>
                                  <Typography variant="body2" component="div">
                                    • {t('agtUps.marketAnalysis.marketInsights.components.growthOpportunity')}
                                    <br />
                                    • {t('agtUps.marketAnalysis.marketInsights.components.marketThreat')}
                                    <br />
                                    • {t('agtUps.marketAnalysis.marketInsights.components.strategicRecommendation')}
                                    <br />
                                    • Relevant trends
                                  </Typography>
                                </Alert>

                                <Box
                                  sx={{
                                    textAlign: 'center',
                                    py: 4,
                                    backgroundColor: alpha(navColors.background, 0.5),
                                    borderRadius: 2
                                  }}
                                >
                                  <Typography sx={{ color: navColors.textSecondary, mb: 1 }}>
                                    {t('agtUps.marketAnalysis.marketInsights.noInsights.title')}
                                  </Typography>
                                  <Typography variant="body2" sx={{ color: navColors.textSecondary, opacity: 0.8 }}>
                                    {t('agtUps.marketAnalysis.marketInsights.noInsights.description')}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </StyledPaper>
                        </Grid>
                      </Grid>
                    </>
                  )}

                  {/* 2. MARKET SHARE TRENDS => Only the chart */}
                  {analysisType === 'marketShareTrends' && (
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <StyledPaper style={{ position: 'relative' }}>
                          {updating && (
                            <LoadingOverlay>
                              <AppleSpinner />
                            </LoadingOverlay>
                          )}
                          <Box sx={{ mb: 3 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                              <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
                                {t('agtUps.marketAnalysis.marketShareTrends.title')}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title={t('agtUps.marketAnalysis.marketShareTrends.chips.marketShare')} arrow>
                                  <StyledChip
                                    size="small"
                                    icon={<Eye size={14} />}
                                    label={t('agtUps.marketAnalysis.marketShareTrends.chips.marketShare')}
                                  />
                                </Tooltip>
                                <Tooltip title={t('agtUps.marketAnalysis.marketShareTrends.chips.competitors')} arrow>
                                  <StyledChip
                                    size="small"
                                    icon={<Users size={14} />}
                                    label={t('agtUps.marketAnalysis.marketShareTrends.chips.competitors')}
                                  />
                                </Tooltip>
                                <Tooltip title={t('agtUps.marketAnalysis.marketShareTrends.chips.marketPotential')} arrow>
                                  <StyledChip
                                    size="small"
                                    icon={<Target size={14} />}
                                    label={t('agtUps.marketAnalysis.marketShareTrends.chips.marketPotential')}
                                  />
                                </Tooltip>
                              </Box>
                            </Box>
                            <Alert
                              severity="info"
                              sx={{
                                backgroundColor: alpha(navColors.accent, 0.1),
                                color: navColors.textDefault
                              }}
                            >
                              <Typography variant="body2">
                                {t('agtUps.marketAnalysis.marketShareTrends.description')}
                              </Typography>
                            </Alert>
                          </Box>

                          <Box sx={{ height: 320 }}>
                            {formattedMarketData?.marketShare?.data ? (
                              <ResponsiveContainer width="100%" height="100%">
                                <RechartsLineChart data={formattedMarketData.marketShare.data}>
                                  <CartesianGrid strokeDasharray="3 3" stroke={alpha(navColors.textDefault, 0.2)} />
                                  <XAxis dataKey="name" tick={{ fill: navColors.textDefault }} />
                                  <YAxis tick={{ fill: navColors.textDefault }} />
                                  <RechartsTooltip
                                    contentStyle={{
                                      backgroundColor: alpha(navColors.cardBackground, 0.8),
                                      borderRadius: '8px',
                                      color: navColors.textDefault
                                    }}
                                  />
                                  <Line
                                    type="monotone"
                                    dataKey="marketShare"
                                    name={t('agtUps.marketAnalysis.marketShareTrends.chips.marketShare')}
                                    stroke={navColors.accent}
                                    strokeWidth={2}
                                    dot={{ r: 4 }}
                                    activeDot={{ r: 8 }}
                                  />
                                  <Line
                                    type="monotone"
                                    dataKey="competitors"
                                    name={t('agtUps.marketAnalysis.marketShareTrends.chips.competitors')}
                                    stroke={navColors.secondary}
                                    strokeWidth={2}
                                    dot={{ r: 4 }}
                                  />
                                  <Line
                                    type="monotone"
                                    dataKey="potential"
                                    name={t('agtUps.marketAnalysis.marketShareTrends.chips.marketPotential')}
                                    stroke={navColors.success}
                                    strokeWidth={2}
                                    dot={{ r: 4 }}
                                  />
                                  <Legend wrapperStyle={{ color: navColors.textDefault }} />
                                </RechartsLineChart>
                              </ResponsiveContainer>
                            ) : (
                              <Box
                                sx={{
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Typography sx={{ color: navColors.textSecondary }}>
                                  {t('agtUps.marketAnalysis.marketShareTrends.noData')}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </StyledPaper>
                      </Grid>
                    </Grid>
                  )}

                  {/* 3. MARKET POSITION => "Market Position" section */}
                  {analysisType === 'marketPosition' && (
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <StyledPaper>
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                              {t('agtUps.marketAnalysis.marketPosition.title')}
                            </Typography>
                            <Alert
                              severity="info"
                              sx={{
                                backgroundColor: alpha(navColors.accent, 0.1),
                                color: navColors.textDefault
                              }}
                            >
                              <Typography variant="body2">
                                {t('agtUps.marketAnalysis.marketPosition.description')}
                              </Typography>
                            </Alert>
                          </Box>

                          {marketData ? (
                            <Box sx={{ display: 'grid', rowGap: 2 }}>
                              <Tooltip title={t('agtUps.marketAnalysis.marketPosition.tooltips.marketShare')} arrow placement="top">
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: 1,
                                    cursor: 'help'
                                  }}
                                >
                                  <Typography variant="body2">{t('agtUps.marketAnalysis.marketPosition.metrics.marketShare')}</Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {metrics?.yearOverYear?.growth > 0 ? (
                                      <ArrowUpRight size={16} style={{ color: navColors.success }} />
                                    ) : (
                                      <ArrowDownRight size={16} style={{ color: navColors.error }} />
                                    )}
                                    <Typography variant="body2" sx={{ color: metrics?.yearOverYear?.growth > 0 ? navColors.success : navColors.error }}>
                                      {marketData.marketShare}%
                                    </Typography>
                                  </Box>
                                </Box>
                              </Tooltip>
                              <StyledLinearProgress variant="determinate" value={marketData.marketShare} />

                              <Tooltip title={t('agtUps.marketAnalysis.marketPosition.tooltips.competitivePosition')} arrow placement="top">
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: 1,
                                    cursor: 'help'
                                  }}
                                >
                                  <Typography variant="body2">{t('agtUps.marketAnalysis.marketPosition.metrics.competitivePosition')}</Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <ArrowUpRight size={16} style={{ color: navColors.success }} />
                                    <Typography variant="body2" sx={{ color: navColors.success }}>
                                      {metrics?.competitivePosition || 0}%
                                    </Typography>
                                  </Box>
                                </Box>
                              </Tooltip>
                              <StyledLinearProgress variant="determinate" value={metrics?.competitivePosition || 0} />

                              <Tooltip title={t('agtUps.marketAnalysis.marketPosition.tooltips.marketPenetration')} arrow placement="top">
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    marginBottom: 1,
                                    cursor: 'help'
                                  }}
                                >
                                  <Typography variant="body2">{t('agtUps.marketAnalysis.marketPosition.metrics.marketPenetration')}</Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {metrics?.marketPenetration > 0 ? (
                                      <ArrowUpRight size={16} style={{ color: navColors.success }} />
                                    ) : (
                                      <ArrowDownRight size={16} style={{ color: navColors.error }} />
                                    )}
                                    <Typography variant="body2" sx={{ color: metrics?.marketPenetration > 0 ? navColors.success : navColors.error }}>
                                      {metrics?.marketPenetration || 0}%
                                    </Typography>
                                  </Box>
                                </Box>
                              </Tooltip>
                              <StyledLinearProgress variant="determinate" value={metrics?.marketPenetration || 0} />
                            </Box>
                          ) : (
                            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                              <AppleSpinner />
                            </Box>
                          )}
                        </StyledPaper>
                      </Grid>
                    </Grid>
                  )}

                  {/* 4. KEY METRICS => "Key Metrics" section */}
                  {analysisType === 'keyMetrics' && (
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <StyledPaper>
                          <Typography variant="h6" sx={{ fontWeight: 600, marginBottom: 2 }}>
                            {t('agtUps.marketAnalysis.keyMetrics.title')}
                          </Typography>
                          <Alert
                            severity="info"
                            sx={{
                              mb: 3,
                              backgroundColor: alpha(navColors.accent, 0.1),
                              color: navColors.textDefault,
                              '& .MuiAlert-icon': {
                                color: navColors.accent
                              }
                            }}
                          >
                            <Typography variant="body2">
                              {t('agtUps.marketAnalysis.keyMetrics.description')}
                            </Typography>
                          </Alert>
                          <Grid container spacing={2}>
                            <Grid item xs={6}>
                              <Tooltip title={t('agtUps.marketAnalysis.keyMetrics.marketFit.tooltip')} arrow placement="top">
                                <MetricCard sx={{ cursor: 'help' }}>
                                  <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                    <UserCheck size={20} style={{ color: navColors.accent }} />
                                    <Typography variant="body2">{t('agtUps.marketAnalysis.keyMetrics.marketFit.title')}</Typography>
                                  </Box>
                                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {metrics?.marketFit || 'N/A'}%
                                  </Typography>
                                </MetricCard>
                              </Tooltip>
                            </Grid>

                            <Grid item xs={6}>
                              <Tooltip title={t('agtUps.marketAnalysis.keyMetrics.growthPotential.tooltip')} arrow placement="top">
                                <MetricCard sx={{ cursor: 'help' }}>
                                  <Box sx={{ display: 'flex', gap: 1, mb: 1, alignItems: 'center' }}>
                                    <ShoppingCart size={20} style={{ color: navColors.secondary }} />
                                    <Typography variant="body2">{t('agtUps.marketAnalysis.keyMetrics.growthPotential.title')}</Typography>
                                  </Box>
                                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {metrics?.growthPotential?.potential || 'N/A'}%
                                  </Typography>
                                  {metrics?.growthPotential?.confidence && (
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                      <Typography variant="caption" sx={{ color: navColors.textSecondary }}>
                                        {t('agtUps.marketAnalysis.keyMetrics.growthPotential.confidence', { confidence: metrics.growthPotential.confidence })}
                                      </Typography>
                                    </Box>
                                  )}
                                </MetricCard>
                              </Tooltip>
                            </Grid>
                          </Grid>
                        </StyledPaper>
                      </Grid>
                    </Grid>
                  )}

                  {/* 5. MARKET SHARE DISTRIBUTION => PieChart */}
                  {analysisType === 'marketShareDistribution' && (
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <StyledPaper>
                          <Box sx={{ mb: 3 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                              {t('agtUps.marketAnalysis.marketShareDistribution.title')}
                            </Typography>
                            <Alert
                              severity="info"
                              sx={{
                                backgroundColor: alpha(navColors.accent, 0.1),
                                color: navColors.textDefault,
                                mb: 2,
                                '& .MuiAlert-icon': {
                                  color: navColors.accent
                                }
                              }}
                            >
                              <Typography variant="body2">
                                {t('agtUps.marketAnalysis.marketShareDistribution.description')}
                              </Typography>
                            </Alert>
                          </Box>

                          <Box sx={{ height: 320 }}>
                            {formattedMarketData?.competitors ? (
                              <ResponsiveContainer width="100%" height="100%">
                                <RechartsPieChart>
                                  <Pie
                                    data={formattedMarketData.competitors}
                                    cx="50%"
                                    cy="50%"
                                    innerRadius={60}
                                    outerRadius={100}
                                    dataKey="value"
                                    labelLine={false}
                                    label={({ name, percent }) =>
                                      `${name} (${(percent * 100).toFixed(1)}%)`
                                    }
                                  >
                                    {formattedMarketData.competitors.map((entry, index) => (
                                      <Cell
                                        key={`cell-${index}`}
                                        fill={competitorColors[index % competitorColors.length]}
                                        className="transition-all duration-300 hover:opacity-80"
                                      />
                                    ))}
                                  </Pie>
                                  <RechartsTooltip
                                    content={({ active, payload }) => {
                                      if (!active || !payload?.[0]) return null;
                                      const data = payload[0].payload;
                                      return (
                                        <Box
                                          sx={{
                                            backgroundColor: alpha(navColors.cardBackground, 0.9),
                                            p: 2,
                                            borderRadius: '8px',
                                            color: navColors.textDefault
                                          }}
                                        >
                                          <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                            {data.name}
                                          </Typography>
                                          <Typography variant="body2">
                                            {t('agtUps.marketAnalysis.marketShareDistribution.tooltip.marketShare', { value: data.value.toFixed(1) })}
                                          </Typography>
                                          {data.strengths?.length > 0 && (
                                            <Box sx={{ mt: 1 }}>
                                              <Typography variant="caption" sx={{ color: navColors.success }}>
                                                {t('agtUps.marketAnalysis.marketShareDistribution.tooltip.strengths', { strengths: data.strengths.join(', ') })}
                                              </Typography>
                                            </Box>
                                          )}
                                          {data.weaknesses?.length > 0 && (
                                            <Box sx={{ mt: 1 }}>
                                              <Typography variant="caption" sx={{ color: navColors.error }}>
                                                {t('agtUps.marketAnalysis.marketShareDistribution.tooltip.weaknesses', { weaknesses: data.weaknesses.join(', ') })}
                                              </Typography>
                                            </Box>
                                          )}
                                        </Box>
                                      );
                                    }}
                                  />
                                  <Legend verticalAlign="bottom" height={36} wrapperStyle={{ color: navColors.textDefault }} />
                                </RechartsPieChart>
                              </ResponsiveContainer>
                            ) : (
                              <Box
                                sx={{
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center'
                                }}
                              >
                                <Typography sx={{ color: navColors.textSecondary }}>
                                  {t('agtUps.marketAnalysis.marketShareDistribution.noData')}
                                </Typography>
                              </Box>
                            )}
                          </Box>

                          {/* Interactive legend */}
                          <Box sx={{ mt: 2, textAlign: 'center' }}>
                            {isMobile ? (
                              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                                <TouchAppIcon style={{ color: navColors.textSecondary }} />
                                <Typography variant="caption" sx={{ color: navColors.textSecondary }}>
                                  {t('agtUps.marketAnalysis.marketShareDistribution.tapToView')}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="caption" sx={{ color: navColors.textSecondary }}>
                                {t('agtUps.marketAnalysis.marketShareDistribution.clickToView')}
                              </Typography>
                            )}
                          </Box>
                        </StyledPaper>
                      </Grid>
                    </Grid>
                  )}

                  {/* 6. MARKET INSIGHTS => "Market Insights" section */}
                  {analysisType === 'marketInsights' && (
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <StyledPaper>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                              {t('agtUps.marketAnalysis.marketInsights.title')}
                            </Typography>
                            <StyledChip label={t('agtUps.marketAnalysis.marketInsights.aiGenerated')} size="small" />
                          </Box>

                          {marketData?.insights ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                              <Alert
                                severity="info"
                                sx={{
                                  backgroundColor: alpha(navColors.accent, 0.1),
                                  color: navColors.textDefault,
                                  mb: 3,
                                  '& .MuiAlert-icon': {
                                    color: navColors.accent
                                  }
                                }}
                              >
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                  {t('agtUps.marketAnalysis.marketInsights.description')}
                                </Typography>
                              </Alert>

                              {/* Growth Opportunities */}
                              {marketData.insights.opportunities?.map((opportunity, index) => (
                                <Alert
                                  key={index}
                                  severity="info"
                                  icon={<TrendingUp />}
                                  sx={{
                                    backgroundColor: alpha(navColors.accent, 0.1),
                                    color: navColors.textDefault,
                                    backdropFilter: 'blur(8px)'
                                  }}
                                >
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {t('agtUps.marketAnalysis.marketInsights.components.growthOpportunity')}
                                  </Typography>
                                  <Typography variant="body2">
                                    {opportunity.description}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                    <StyledChip 
                                      label={t('agtUps.marketAnalysis.marketInsights.components.impact', { value: opportunity.impact })} 
                                      size="small" 
                                    />
                                    {opportunity.timeframe && (
                                      <Chip
                                        label={opportunity.timeframe}
                                        size="small"
                                        sx={{ borderRadius: '8px' }}
                                      />
                                    )}
                                  </Box>
                                </Alert>
                              ))}

                              {/* Potential Threats */}
                              {marketData.insights.threats?.map((threat, index) => (
                                <Alert
                                  key={index}
                                  severity="warning"
                                  icon={<AlertCircle />}
                                  sx={{
                                    backgroundColor: alpha(navColors.warning, 0.1),
                                    color: navColors.textDefault,
                                    backdropFilter: 'blur(8px)'
                                  }}
                                >
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {t('agtUps.marketAnalysis.marketInsights.components.marketThreat')}
                                  </Typography>
                                  <Typography variant="body2">
                                    {threat.description}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                    <StyledChip
                                      label={t('agtUps.marketAnalysis.marketInsights.components.severity', { value: threat.severity })}
                                      size="small"
                                      sx={{
                                        backgroundColor: alpha(navColors.warning, 0.15),
                                        color: navColors.warning
                                      }}
                                    />
                                    <StyledChip
                                      label={t('agtUps.marketAnalysis.marketInsights.components.probability', { value: threat.probability })}
                                      size="small"
                                      sx={{
                                        backgroundColor: alpha(navColors.warning, 0.15),
                                        color: navColors.warning
                                      }}
                                    />
                                  </Box>
                                </Alert>
                              ))}

                              {/* Strategic Recommendations */}
                              {marketData.insights.recommendations?.map((recommendation, index) => (
                                <Alert
                                  key={index}
                                  severity="success"
                                  icon={<Info />}
                                  sx={{
                                    backgroundColor: alpha(navColors.success, 0.1),
                                    color: navColors.textDefault,
                                    backdropFilter: 'blur(8px)'
                                  }}
                                >
                                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    {t('agtUps.marketAnalysis.marketInsights.components.strategicRecommendation')}
                                  </Typography>
                                  <Typography variant="body2">
                                    {recommendation.strategy}
                                  </Typography>
                                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                    <StyledChip
                                      label={t('agtUps.marketAnalysis.marketInsights.components.priority', { value: recommendation.priority })}
                                      size="small"
                                      sx={{
                                        backgroundColor: alpha(navColors.success, 0.15),
                                        color: navColors.success
                                      }}
                                    />
                                    <Chip
                                      label={recommendation.expectedImpact}
                                      size="small"
                                      sx={{ borderRadius: '8px' }}
                                    />
                                  </Box>
                                </Alert>
                              ))}
                            </Box>
                          ) : (
                            <Box>
                              <Alert
                                severity="info"
                                sx={{
                                  backgroundColor: alpha(navColors.accent, 0.1),
                                  color: navColors.textDefault,
                                  mb: 3,
                                  '& .MuiAlert-icon': {
                                    color: navColors.accent
                                  }
                                }}
                              >
                                <Typography variant="body2" sx={{ mb: 2 }}>
                                  {t('agtUps.marketAnalysis.marketInsights.description')}
                                </Typography>
                                <Typography variant="body2" component="div">
                                  • {t('agtUps.marketAnalysis.marketInsights.components.growthOpportunity')}
                                  <br />
                                  • {t('agtUps.marketAnalysis.marketInsights.components.marketThreat')}
                                  <br />
                                  • {t('agtUps.marketAnalysis.marketInsights.components.strategicRecommendation')}
                                  <br />
                                  • Relevant trends
                                </Typography>
                              </Alert>

                              <Box
                                sx={{
                                  textAlign: 'center',
                                  py: 4,
                                  backgroundColor: alpha(navColors.background, 0.5),
                                  borderRadius: 2
                                }}
                              >
                                <Typography sx={{ color: navColors.textSecondary, mb: 1 }}>
                                  {t('agtUps.marketAnalysis.marketInsights.noInsights.title')}
                                </Typography>
                                <Typography variant="body2" sx={{ color: navColors.textSecondary, opacity: 0.8 }}>
                                  {t('agtUps.marketAnalysis.marketInsights.noInsights.description')}
                                </Typography>
                              </Box>
                            </Box>
                          )}
                        </StyledPaper>
                      </Grid>
                    </Grid>
                  )}
                </>
              )
            )}
          </>
        )}
      </Box>
    </StyledContainer>
  );
};

export default MarketAnalysis;